/**
 * Time formatting for alert surfaces. Ported from `feat/alerts-list-querying`.
 *
 * Both functions exist because the obvious alternatives are subtly wrong for a
 * queue an operator watches across sites.
 *
 * These are plain functions called from render paths in several components, so
 * they read the active language off the i18n singleton rather than taking a `t`
 * — every caller already re-renders on a language change.
 */
import i18n from "@/shared/lib/i18n/index.ts";
import { activeLocale } from "@/shared/lib/i18n/formatters.ts";

/**
 * `formatters.ts` has no timezone-override helper, and this rendering must be in
 * the ALERT's zone rather than the viewer's. `en-GB` rather than `en` keeps the
 * English day-first order; the month is a name in both languages, so there is no
 * dd/MM-vs-MM/dd ambiguity to get wrong here.
 */
const siteTimeLocale = (): string => (activeLocale() === "pt-BR" ? "pt-BR" : "en-GB");

/**
 * Site-local time honesty: render the wall clock at the alert's OWN location,
 * never silently substituted with the browser's (or the currently-selected
 * organization's) zone. An operator working two sites would otherwise read one
 * site's alerts in the other's local time and mis-judge how old they are.
 *
 * When the alert carries no `location_timezone` there is no honest local
 * rendering, so the absolute instant is shown with an explicit "UTC" marker
 * rather than a plausible-looking local time.
 */
export const formatSiteTime = (
  iso: string | null | undefined,
  tz: string | null | undefined,
): string => {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  const options: Intl.DateTimeFormatOptions = {
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  };

  if (tz) {
    try {
      return new Intl.DateTimeFormat(siteTimeLocale(), { ...options, timeZone: tz }).format(date);
    } catch {
      // An unknown/invalid IANA zone falls through to the marked UTC rendering.
    }
  }
  return `${new Intl.DateTimeFormat(siteTimeLocale(), { ...options, timeZone: "UTC" }).format(date)} UTC`;
};

/**
 * Elapsed time since an instant → "8m" / "1h 48m" / "2d".
 *
 * `nowIso` is the server's `as_of` from the queue response. Measuring queue age
 * against the SERVER's clock rather than `Date.now()` keeps a skewed browser
 * from reporting work as older or fresher than it is — and the queue's whole
 * purpose is ranking work by how long it has waited.
 */
export const waitedFor = (iso: string | null | undefined, nowIso?: string): string => {
  if (!iso) return "—";
  const now = nowIso ? new Date(nowIso).getTime() : Date.now();
  const ms = now - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";

  const minutes = Math.round(ms / 60_000);
  if (minutes < 60) return i18n.t("time.minutes", { ns: "alerts", n: minutes });
  const hours = Math.floor(minutes / 60);
  const remainder = minutes % 60;
  if (hours < 36) {
    return remainder
      ? i18n.t("time.hoursMinutes", { ns: "alerts", hours, minutes: remainder })
      : i18n.t("time.hours", { ns: "alerts", n: hours });
  }
  return i18n.t("time.days", { ns: "alerts", n: Math.round(hours / 24) });
};
